import { Injectable, signal } from '@angular/core';

export type Theme = 'light' | 'dark';
export type Accent = 'blue' | 'red' | 'pink' | 'green' | 'purple' | 'orange' | 'white';

const ACCENTS: Accent[] = ['blue', 'red', 'pink', 'green', 'purple', 'orange', 'white'];

export interface ThemePreset {
  id: string;
  label: string;
  theme: Theme;
  accent: Accent;
}

/// Presets: modo (claro/oscuro) × acento (azul/rojo/rosa/verde/morado/naranja) sobre el tema Fluent de DevExtreme.
/// La base Fluent (light/dark) la dan los CSS de DevExtreme; el acento lo aplica themes.css por data-accent.
export const THEME_PRESETS: ThemePreset[] = [
  { id: 'light-white',  label: 'Claro Blanco',   theme: 'light', accent: 'white' },
  { id: 'light-blue',   label: 'Claro Azul',     theme: 'light', accent: 'blue' },
  { id: 'light-red',    label: 'Claro Rojo',     theme: 'light', accent: 'red' },
  { id: 'light-pink',   label: 'Claro Rosa',     theme: 'light', accent: 'pink' },
  { id: 'light-green',  label: 'Claro Verde',    theme: 'light', accent: 'green' },
  { id: 'light-purple', label: 'Claro Morado',   theme: 'light', accent: 'purple' },
  { id: 'light-orange', label: 'Claro Naranja',  theme: 'light', accent: 'orange' },
  { id: 'dark-white',   label: 'Oscuro Neutro',  theme: 'dark',  accent: 'white' },
  { id: 'dark-blue',    label: 'Oscuro Azul',    theme: 'dark',  accent: 'blue' },
  { id: 'dark-red',     label: 'Oscuro Rojo',    theme: 'dark',  accent: 'red' },
  { id: 'dark-pink',    label: 'Oscuro Rosa',    theme: 'dark',  accent: 'pink' },
  { id: 'dark-green',   label: 'Oscuro Verde',   theme: 'dark',  accent: 'green' },
  { id: 'dark-purple',  label: 'Oscuro Morado',  theme: 'dark',  accent: 'purple' },
  { id: 'dark-orange',  label: 'Oscuro Naranja', theme: 'dark',  accent: 'orange' },
];

const THEME_KEY = 'pl-theme';
const ACCENT_KEY = 'pl-accent';

/// Valores por defecto cuando el usuario aún no ha elegido un tema.
export const DEFAULT_THEME: Theme = 'light';
export const DEFAULT_ACCENT: Accent = 'white';

/// Lectura segura del tema guardado (la usa main.ts antes del render para evitar parpadeo).
export function readStoredTheme(): Theme {
  try {
    const stored = localStorage.getItem(THEME_KEY);
    return stored === 'dark' || stored === 'light' ? stored : DEFAULT_THEME;
  } catch { return DEFAULT_THEME; }
}

/// Lectura segura del acento guardado; si no existe o es inválido, usa el default.
export function readStoredAccent(): Accent {
  try {
    const stored = localStorage.getItem(ACCENT_KEY) as Accent | null;
    return stored && ACCENTS.includes(stored) ? stored : DEFAULT_ACCENT;
  } catch { return DEFAULT_ACCENT; }
}

/// Estado de tema con signals + persistencia en localStorage (por usuario/navegador).
@Injectable({ providedIn: 'root' })
export class ThemeService {
  readonly theme = signal<Theme>(this.getInitialTheme());
  readonly accent = signal<Accent>(this.getInitialAccent());

  private getInitialTheme(): Theme {
    return readStoredTheme();
  }

  private getInitialAccent(): Accent {
    return readStoredAccent();
  }

  private applyToDOM(theme: Theme, accent: Accent): void {
    document.documentElement.setAttribute('data-theme', theme);
    document.documentElement.setAttribute('data-accent', accent);
  }

  setPreset(id: string): void {
    const p = THEME_PRESETS.find(x => x.id === id);
    if (!p) { return; }
    this.setThemeAndAccent(p.theme, p.accent);
  }

  setThemeAndAccent(theme: Theme, accent: Accent): void {
    localStorage.setItem(THEME_KEY, theme);
    localStorage.setItem(ACCENT_KEY, accent);
    this.applyToDOM(theme, accent);
    this.theme.set(theme);
    this.accent.set(accent);
  }

  setAccent(accent: Accent): void {
    this.setThemeAndAccent(this.theme(), accent);
  }

  toggleTheme(): void {
    const next: Theme = this.theme() === 'light' ? 'dark' : 'light';
    this.setThemeAndAccent(next, this.accent());
  }

  /// id del preset actual (para marcar el activo en el selector).
  currentPresetId(): string {
    return `${this.theme()}-${this.accent()}`;
  }
}
